// Phase current sensor — a current-over-time graph for a From/To range plus the
// power consumption (kWh / Wh / ₹) for that same range. Works for single phase
// (one line) and three phase (R/Y/B). Data from
// GetCurrentSenseByUniqueNumberAndDateOnly (per day), filtered to the range.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card, Box, Typography,
  CircularProgress, Chip, TextField, Button, Divider, FormControlLabel, Checkbox,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeviceCardMenu from './DeviceCardMenu';
import BoltIcon from '@mui/icons-material/Bolt';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { getCurrentSense } from '../api/api';
import DeviceStatusDot from './DeviceStatusDot';

const PHASE_META = {
  1: { color: '#e53935', label: 'R' },
  2: { color: '#fbc02d', label: 'Y' },
  3: { color: '#1e88e5', label: 'B' },
};
const RATE = 15; // ₹ per kWh (backend convention)

// Parse a reading's DateTime as naive-local (drop the API's 'Z') so it aligns
// with the local datetime-local range inputs.
const parseLocal = (s) => new Date(String(s).replace('Z', '').replace('T', ' '));
const pad = (n) => String(n).padStart(2, '0');
const dateOnly = () => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`; };
const nowLocalStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const fmtTick = (iso, multiDay) => {
  const d = parseLocal(iso);
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return multiDay ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${time}` : time;
};

// Per-(device, day) cache so re-viewing a range doesn't re-hit the slow query.
// Past days are immutable (long TTL); today keeps a short TTL for fresh readings.
const dayCache = new Map();
async function fetchDay(uno, ds, isToday) {
  const key = `${uno}|${ds}`;
  const hit = dayCache.get(key);
  const ttl = isToday ? 30000 : 6 * 3600000;
  if (hit && Date.now() - hit.at < ttl) return hit.rows;
  const res = await getCurrentSense(uno, ds).catch(() => null);
  const rows = res && Array.isArray(res.data) ? res.data : [];
  dayCache.set(key, { rows, at: Date.now() });
  return rows;
}

function downsample(rows, max = 160) {
  if (rows.length <= max) return rows;
  const step = Math.ceil(rows.length / max);
  return rows.filter((_, i) => i % step === 0);
}

// Energy (kWh) = Σ power × interval, per phase then summed. Offline gaps capped
// to 1 min so a break doesn't over-count.
function energyKWh(rows) {
  const byPhase = {};
  rows.forEach((r) => { const p = r.phase; (byPhase[p] = byPhase[p] || []).push(r); });
  let wh = 0;
  Object.values(byPhase).forEach((list) => {
    for (let i = 0; i < list.length - 1; i += 1) {
      let h = (parseLocal(list[i + 1].DateTime) - parseLocal(list[i].DateTime)) / 3600000;
      if (h <= 0) continue; // eslint-disable-line no-continue
      if (h > 2 / 60) h = 1 / 60;
      wh += (Number(list[i].power) || 0) * h;
    }
  });
  return wh / 1000;
}

export default function PhaseControlCard({ device }) {
  const theme = useTheme();
  const uno = device.UniqueNumber;
  const [data, setData] = useState([]);
  const [phases, setPhases] = useState([1]);
  const [latest, setLatest] = useState({});
  const [latestTime, setLatestTime] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [loading, setLoading] = useState(true);

  const [from, setFrom] = useState(`${dateOnly()}T00:00`);
  const [to, setTo] = useState(`${dateOnly()}T23:59`);
  const [live, setLive] = useState(false); // when on, To = now + auto-refresh

  // Load the readings for [from, to], then build graph + latest + consumption.
  const loadRange = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const fromD = new Date(from);
      // Live mode uses "now" as the To (and shows it in the field); otherwise the
      // graph + consumption follow EXACTLY the From/To on screen.
      let toStr = to;
      if (live) { toStr = nowLocalStr(); setTo(toStr); }
      const toD = new Date(toStr);
      if (Number.isNaN(+fromD) || Number.isNaN(+toD) || toD <= fromD) {
        setConsumption({ error: 'Pick a valid From/To range' });
        setData([]);
        return;
      }
      const days = [];
      const cur = new Date(fromD.getFullYear(), fromD.getMonth(), fromD.getDate());
      const end = new Date(toD.getFullYear(), toD.getMonth(), toD.getDate());
      while (cur <= end && days.length < 62) {
        days.push(`${cur.getFullYear()}-${cur.getMonth() + 1}-${cur.getDate()}`);
        cur.setDate(cur.getDate() + 1);
      }
      const now2 = new Date();
      const todayDs = `${now2.getFullYear()}-${now2.getMonth() + 1}-${now2.getDate()}`;
      // Fetch all days in parallel, cached per day.
      const results = await Promise.all(days.map((ds) => fetchDay(uno, ds, ds === todayDs)));
      const all = results.flat();
      const asc = all
        .filter((r) => { const t = parseLocal(r.DateTime); return t >= fromD && t <= toD; })
        .sort((a, b) => parseLocal(a.DateTime) - parseLocal(b.DateTime));

      const present = [...new Set(asc.map((r) => Number(r.phase)))].sort((a, b) => a - b);
      setPhases(present.length ? present : [1]);

      const multiDay = days.length > 1;
      const byTime = new Map();
      asc.forEach((r) => {
        const k = r.DateTime;
        if (!byTime.has(k)) byTime.set(k, { iso: k, t: fmtTick(k, multiDay) });
        byTime.get(k)[`p${Number(r.phase)}`] = Number(r.current) || 0;
      });
      setData(downsample([...byTime.values()]));

      if (asc.length) {
        const lastIso = asc[asc.length - 1].DateTime;
        const lat = {};
        asc.filter((r) => r.DateTime === lastIso).forEach((r) => {
          lat[Number(r.phase)] = { current: Number(r.current) || 0, power: Number(r.power) || 0 };
        });
        setLatest(lat);
        setLatestTime(fmtTick(lastIso, multiDay));
      } else {
        setLatest({});
        setLatestTime(null);
      }

      const kwh = energyKWh(asc);
      setConsumption({ kwh, wh: kwh * 1000, bill: kwh * RATE });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [uno, from, to, live]);

  // Keep a ref to the latest loader so timers always use the current range.
  const loadRef = useRef(loadRange);
  loadRef.current = loadRange;

  // Load on mount, device change, or when Live is toggled. Typing in the range
  // fields does NOT reload — you apply it with Get (so the data always matches
  // what's on screen, never the "current situation").
  useEffect(() => {
    loadRef.current(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno, live]);

  // Live mode only: refresh every 30s using the latest range (To = now).
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => loadRef.current(true), 30000);
    return () => clearInterval(id);
  }, [live]);

  const axis = theme.palette.text.secondary;
  const grid = theme.palette.divider;
  const multi = phases.length > 1;

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BoltIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
        <Typography fontWeight={700} color="text.primary" noWrap sx={{ flex: 1 }}>{device.DeviceName || uno}</Typography>
        <DeviceStatusDot device={device} />
        <DeviceCardMenu
          device={device}
          extraItems={[{ label: 'Refresh', icon: <RefreshIcon fontSize="small" />, onClick: () => loadRange(false) }]}
        />
      </Box>

      {multi ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {phases.map((p) => (
            <Chip key={p} size="small"
              label={`${PHASE_META[p]?.label || p} ${latest[p] ? latest[p].current.toFixed(2) : '--'} A`}
              sx={{ bgcolor: PHASE_META[p]?.color, color: '#000', fontWeight: 700 }}
            />
          ))}
          {latestTime && <Typography variant="caption" color="text.secondary">· {latestTime}</Typography>}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            {latest[1] ? latest[1].current.toFixed(2) : '--'}
            <Typography component="span" variant="body2" color="text.secondary"> A</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {latest[1] ? `${latest[1].power.toFixed(0)} W` : ''}{latestTime ? ` · ${latestTime}` : ''}
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 150 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}><CircularProgress size={22} /></Box>
        ) : data.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
            <Typography variant="body2" color="text.secondary">No readings in this range</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="t" tick={{ fill: axis, fontSize: 10 }} stroke={grid} minTickGap={40} />
              <YAxis tick={{ fill: axis, fontSize: 10 }} stroke={grid} width={38} unit="A" />
              <Tooltip
                contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${grid}`, borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: theme.palette.text.secondary }}
                itemStyle={{ color: theme.palette.text.primary }}
                formatter={(v, name) => [`${v} A`, name]}
              />
              {multi && <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />}
              {phases.map((p) => (
                <Line key={p} type="monotone" dataKey={`p${p}`} name={PHASE_META[p]?.label || `P${p}`}
                  stroke={PHASE_META[p]?.color} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* From/To range → graph above + consumption below both follow it. */}
      <Divider sx={{ mt: 0.5 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
        <TextField type="datetime-local" size="small" label="From" value={from}
          onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
          inputProps={{ style: { fontSize: 12 } }} sx={{ flex: 1, minWidth: 150 }} />
        <TextField type="datetime-local" size="small" label={live ? 'To (live: now)' : 'To'} value={to}
          onChange={(e) => setTo(e.target.value)} disabled={live} InputLabelProps={{ shrink: true }}
          inputProps={{ style: { fontSize: 12 } }} sx={{ flex: 1, minWidth: 150 }} />
        <Button size="small" variant="contained" onClick={() => loadRange(false)} disabled={loading || live}>
          {loading ? <CircularProgress size={16} /> : 'Get'}
        </Button>
        <FormControlLabel
          control={<Checkbox size="small" checked={live} onChange={(e) => setLive(e.target.checked)} />}
          label={<Typography variant="caption">Live</Typography>}
          sx={{ mr: 0 }}
        />
      </Box>

      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>Total Consumption</Typography>
      {consumption && consumption.error ? (
        <Typography variant="body2" color="error">{consumption.error}</Typography>
      ) : consumption ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {consumption.kwh.toFixed(3)}<Typography component="span" variant="body2" color="text.secondary"> kWh</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">{Math.round(consumption.wh)} Wh</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: 'success.main' }}>≈ ₹{consumption.bill.toFixed(2)}</Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">--</Typography>
      )}

    </Card>
  );
}
